import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
} from "react";
import type { UserRole } from "./auth-types";
import { localizedConferenceName } from "./conference-names";
import type {
  ConferenceDashboardClient,
  ExpenseRecord,
  PortfolioConferenceSummary,
} from "./portfolio-overview-api";
import { portfolioErrorMessageKey } from "./portfolio-api-errors";
import { RevenueField } from "./RevenueField";
import { RiyalAmount, RiyalSymbol } from "./RiyalAmount";
import { localDateTimeInputValue } from "./revenue-form-state";
import {
  bidiIsolationAttributes,
  isolateText,
  useLocale,
  type MessageKey,
} from "./localization";
import { useRevenueDraft, type RevenueDraftReporter } from "./useRevenueDraft";

const EXPENSE_CATEGORIES: readonly ExpenseRecord["category"][] = [
  "operations",
  "marketing",
  "hospitality",
  "technology",
  "speakers",
  "travel_accommodation",
  "equipment",
  "other",
];

const CATEGORY_KEYS = {
  operations: "expense.category.operations",
  marketing: "expense.category.marketing",
  hospitality: "expense.category.hospitality",
  technology: "expense.category.technology",
  speakers: "expense.category.speakers",
  travel_accommodation: "expense.category.travelAccommodation",
  equipment: "expense.category.equipment",
  other: "expense.category.other",
} as const satisfies Readonly<Record<ExpenseRecord["category"], MessageKey>>;

const NOOP_DRAFT_REPORTER: RevenueDraftReporter = () => undefined;

function parseMinorUnits(value: string): number | null {
  const match = value.trim().match(/^(\d+)(?:\.(\d{1,2}))?$/);
  if (!match?.[1]) return null;
  const amount =
    Number(match[1]) * 100 + Number((match[2] ?? "").padEnd(2, "0"));
  return Number.isSafeInteger(amount) && amount > 0 ? amount : null;
}

function minorUnitsInputValue(value: number): string {
  return (value / 100).toFixed(2);
}

function newIdempotencyKey() {
  return globalThis.crypto.randomUUID();
}

function ExpenseForm({
  conferenceId,
  client,
  onCreated,
  onDraftChange,
}: {
  readonly conferenceId: string;
  readonly client: ConferenceDashboardClient;
  readonly onCreated: (record: ExpenseRecord) => void;
  readonly onDraftChange: RevenueDraftReporter;
}) {
  const { t } = useLocale();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const idempotencyKey = useRef(newIdempotencyKey());
  const [category, setCategory] =
    useState<ExpenseRecord["category"]>("operations");
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [initialTime, setInitialTime] = useState(localDateTimeInputValue);
  const [incurredAt, setIncurredAt] = useState(initialTime);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<MessageKey | null>(null);

  useRevenueDraft(
    "expense-" + conferenceId,
    busy ||
      Boolean(
        title ||
        amount ||
        category !== "operations" ||
        incurredAt !== initialTime,
      ),
    onDraftChange,
  );

  function reset(close: boolean) {
    setCategory("operations");
    setTitle("");
    setAmount("");
    const nextTime = localDateTimeInputValue();
    setInitialTime(nextTime);
    setIncurredAt(nextTime);
    idempotencyKey.current = newIdempotencyKey();
    if (close && detailsRef.current) {
      detailsRef.current.open = false;
      detailsRef.current.querySelector("summary")?.focus();
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const amountMinor = parseMinorUnits(amount);
    const incurredTime = Date.parse(incurredAt);
    if (
      !amountMinor ||
      !Number.isFinite(incurredTime) ||
      title.trim().length < 2
    ) {
      setMessage("expense.form.invalid");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const record = await client.createExpense({
        idempotencyKey: idempotencyKey.current,
        conferenceId,
        title,
        category,
        amountMinor,
        currency: "SAR",
        incurredAt: new Date(incurredTime).toISOString(),
      });
      onCreated(record);
      reset(false);
      setMessage("expense.form.recorded");
    } catch (error: unknown) {
      setMessage(portfolioErrorMessageKey(error, "expense.form.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <details className="revenue-create expense-create" ref={detailsRef}>
      <summary className="button button--primary">
        {t("expense.form.add")}
      </summary>
      <form
        className="revenue-form expense-form"
        onSubmit={submit}
        autoComplete="off"
        aria-busy={busy}
      >
        <div className="revenue-form__heading">
          <h3>{t("expense.form.heading")}</h3>
          <p>{t("finance.form.requiredHint")}</p>
        </div>
        <RevenueField id="expense-category" label={t("expense.form.category")}>
          <select
            id="expense-category"
            name="category"
            disabled={busy}
            value={category}
            onChange={(event) =>
              setCategory(
                event.currentTarget.value as ExpenseRecord["category"],
              )
            }
          >
            {EXPENSE_CATEGORIES.map((value) => (
              <option value={value} key={value}>
                {t(CATEGORY_KEYS[value])}
              </option>
            ))}
          </select>
        </RevenueField>
        <RevenueField
          id="expense-title"
          label={t("expense.form.title")}
          hint={t("expense.form.titleHint")}
        >
          <input
            id="expense-title"
            name="title"
            disabled={busy}
            required
            minLength={2}
            maxLength={160}
            value={title}
            onChange={(event) => setTitle(event.currentTarget.value)}
          />
        </RevenueField>
        <RevenueField
          id="expense-amount"
          label={
            <>
              {t("expense.form.amount")} <RiyalSymbol />
            </>
          }
        >
          <input
            id="expense-amount"
            name="amount"
            disabled={busy}
            required
            inputMode="decimal"
            placeholder={t("finance.placeholder.amount")}
            {...bidiIsolationAttributes()}
            value={amount}
            onChange={(event) => setAmount(event.currentTarget.value)}
          />
        </RevenueField>
        <RevenueField
          id="expense-incurred-at"
          label={t("expense.form.date")}
          hint={t("finance.field.localTimeHint")}
        >
          <input
            id="expense-incurred-at"
            name="incurredAt"
            disabled={busy}
            required
            type="datetime-local"
            {...bidiIsolationAttributes()}
            value={incurredAt}
            onChange={(event) => setIncurredAt(event.currentTarget.value)}
          />
        </RevenueField>
        {message ? (
          <p className="form-message" role="status">
            {t(message)}
          </p>
        ) : null}
        <div className="revenue-form__actions">
          <button
            className="button button--primary"
            type="submit"
            disabled={busy}
          >
            {busy ? t("finance.action.saving") : t("expense.form.record")}
          </button>
          <button
            className="button button--quiet"
            type="button"
            disabled={busy}
            onClick={() => {
              reset(true);
              setMessage(null);
            }}
          >
            {t("finance.form.cancel")}
          </button>
        </div>
      </form>
    </details>
  );
}

function ExpenseEditDialog({
  client,
  record,
  onClose,
  onUpdated,
}: {
  readonly client: ConferenceDashboardClient;
  readonly record: ExpenseRecord;
  readonly onClose: () => void;
  readonly onUpdated: (record: ExpenseRecord) => void;
}) {
  const { t } = useLocale();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const initialIncurredAt = useMemo(
    () => localDateTimeInputValue(new Date(record.incurredAt)),
    [record.incurredAt],
  );
  const [category, setCategory] = useState<ExpenseRecord["category"]>(
    record.category,
  );
  const [title, setTitle] = useState(record.title);
  const [amount, setAmount] = useState(() =>
    minorUnitsInputValue(record.amountMinor),
  );
  const [incurredAt, setIncurredAt] = useState(initialIncurredAt);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<MessageKey | null>(null);
  const dirty =
    category !== record.category ||
    title !== record.title ||
    amount !== minorUnitsInputValue(record.amountMinor) ||
    incurredAt !== initialIncurredAt;

  useRevenueDraft(
    `expense-edit-${record.expenseId}`,
    busy || dirty,
    NOOP_DRAFT_REPORTER,
  );

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    if (typeof dialog.showModal === "function") dialog.showModal();
    else dialog.setAttribute("open", "");
    return () => {
      if (dialog.open && typeof dialog.close === "function") dialog.close();
    };
  }, []);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const amountMinor = parseMinorUnits(amount);
    const incurredTime = Date.parse(incurredAt);
    if (
      !amountMinor ||
      !Number.isFinite(incurredTime) ||
      title.trim().length < 2
    ) {
      setMessage("expense.edit.invalid");
      return;
    }
    setBusy(true);
    setMessage(null);
    try {
      const updated = await client.updateExpense(record.expenseId, {
        title,
        category,
        amountMinor,
        incurredAt: new Date(incurredTime).toISOString(),
        expectedRevision: record.revision,
      });
      onUpdated(updated);
    } catch (error: unknown) {
      setMessage(portfolioErrorMessageKey(error, "expense.edit.failed"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog
      ref={dialogRef}
      className="expense-edit-dialog"
      aria-labelledby="expense-edit-title"
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onClose();
      }}
    >
      <form onSubmit={submit} noValidate aria-busy={busy}>
        <header>
          <div>
            <h2 id="expense-edit-title">{t("expense.edit.heading")}</h2>
            <p>{t("expense.edit.description")}</p>
          </div>
          <button
            className="icon-button"
            type="button"
            aria-label={t("expense.edit.close")}
            disabled={busy}
            onClick={onClose}
          >
            ×
          </button>
        </header>
        <div className="expense-edit-dialog__body">
          <RevenueField
            id="expense-edit-category"
            label={t("expense.form.category")}
          >
            <select
              id="expense-edit-category"
              name="category"
              disabled={busy}
              value={category}
              onChange={(event) =>
                setCategory(
                  event.currentTarget.value as ExpenseRecord["category"],
                )
              }
            >
              {EXPENSE_CATEGORIES.map((value) => (
                <option value={value} key={value}>
                  {t(CATEGORY_KEYS[value])}
                </option>
              ))}
            </select>
          </RevenueField>
          <RevenueField
            id="expense-edit-title-input"
            label={t("expense.form.title")}
            hint={t("expense.form.titleHint")}
          >
            <input
              id="expense-edit-title-input"
              name="title"
              disabled={busy}
              required
              minLength={2}
              maxLength={160}
              value={title}
              onChange={(event) => setTitle(event.currentTarget.value)}
            />
          </RevenueField>
          <RevenueField
            id="expense-edit-amount"
            label={
              <>
                {t("expense.form.amount")} <RiyalSymbol />
              </>
            }
          >
            <input
              id="expense-edit-amount"
              name="amount"
              disabled={busy}
              required
              inputMode="decimal"
              placeholder={t("finance.placeholder.amount")}
              {...bidiIsolationAttributes()}
              value={amount}
              onChange={(event) => setAmount(event.currentTarget.value)}
            />
          </RevenueField>
          <RevenueField
            id="expense-edit-incurred-at"
            label={t("expense.form.date")}
            hint={t("finance.field.localTimeHint")}
          >
            <input
              id="expense-edit-incurred-at"
              name="incurredAt"
              disabled={busy}
              required
              type="datetime-local"
              {...bidiIsolationAttributes()}
              value={incurredAt}
              onChange={(event) => setIncurredAt(event.currentTarget.value)}
            />
          </RevenueField>
          {message ? (
            <p className="form-message" role="alert">
              {t(message)}
            </p>
          ) : null}
        </div>
        <footer>
          <button
            className="button button--secondary"
            type="button"
            disabled={busy}
            onClick={onClose}
          >
            {t("finance.form.cancel")}
          </button>
          <button
            className="button button--primary"
            type="submit"
            disabled={busy || !dirty}
          >
            {busy ? t("finance.action.saving") : t("expense.edit.save")}
          </button>
        </footer>
      </form>
    </dialog>
  );
}

export function ConferenceExpenses({
  conferenceId,
  client,
  canManage,
  onDraftChange = () => undefined,
  onRecordsChange,
  onAvailabilityChange,
  compact = false,
  canEdit = canManage,
}: {
  readonly conferenceId: string;
  readonly client: ConferenceDashboardClient;
  readonly canManage: boolean;
  readonly onDraftChange?: RevenueDraftReporter;
  readonly onRecordsChange?: (records: readonly ExpenseRecord[]) => void;
  readonly onAvailabilityChange?: (
    status: "loading" | "ready" | "error",
  ) => void;
  readonly compact?: boolean;
  readonly canEdit?: boolean;
}) {
  const { date, number, t } = useLocale();
  const [records, setRecords] = useState<readonly ExpenseRecord[]>([]);
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [message, setMessage] = useState<MessageKey>("expense.loadFailed");
  const [operationMessage, setOperationMessage] = useState<MessageKey | null>(
    null,
  );
  const [reversingId, setReversingId] = useState<string | null>(null);
  const [editingRecord, setEditingRecord] = useState<ExpenseRecord | null>(
    null,
  );

  const load = useCallback(
    (signal?: AbortSignal) => {
      if (!conferenceId) {
        setRecords([]);
        setStatus("ready");
        return Promise.resolve();
      }
      setStatus("loading");
      return client
        .loadExpenses(conferenceId, signal)
        .then((loaded) => {
          setRecords(loaded);
          setStatus("ready");
        })
        .catch((error: unknown) => {
          if (!(error instanceof DOMException && error.name === "AbortError")) {
            setMessage(portfolioErrorMessageKey(error, "expense.loadFailed"));
            setStatus("error");
          }
        });
    },
    [client, conferenceId],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    if (status === "ready") onRecordsChange?.(records);
  }, [onRecordsChange, records, status]);

  useEffect(() => {
    onAvailabilityChange?.(status);
  }, [onAvailabilityChange, status]);

  const active = useMemo(
    () => records.filter((record) => record.status === "active"),
    [records],
  );
  const activeTotal = useMemo(
    () => active.reduce((total, record) => total + record.amountMinor, 0),
    [active],
  );

  async function reverse(record: ExpenseRecord) {
    if (reversingId) return;
    setReversingId(record.expenseId);
    setOperationMessage(null);
    try {
      const updated = await client.reverseExpense(record.expenseId);
      setRecords((current) =>
        current.map((candidate) =>
          candidate.expenseId === updated.expenseId ? updated : candidate,
        ),
      );
      setOperationMessage("expense.reverse.recorded");
    } catch (error: unknown) {
      setOperationMessage(
        portfolioErrorMessageKey(error, "expense.reverse.failed"),
      );
    } finally {
      setReversingId(null);
    }
  }

  function updateRecord(updated: ExpenseRecord) {
    setRecords((current) =>
      current.map((candidate) =>
        candidate.expenseId === updated.expenseId ? updated : candidate,
      ),
    );
    setOperationMessage("expense.edit.recorded");
    setEditingRecord(null);
  }

  return (
    <section
      className={compact ? "expenses expenses--compact" : "expenses"}
      aria-labelledby={compact ? "conference-expenses-title" : "expenses-title"}
    >
      <header className="section-heading">
        <div>
          <h2 id={compact ? "conference-expenses-title" : "expenses-title"}>
            {t("expense.heading")}
          </h2>
          <p>{t("expense.description")}</p>
        </div>
      </header>
      <div className="expense-summary" aria-label={t("expense.summary.aria")}>
        <article>
          <span>{t("expense.summary.total")}</span>
          <strong>
            <RiyalAmount minor={activeTotal} fractionDigits={2} />
          </strong>
        </article>
        <article>
          <span>{t("expense.summary.count")}</span>
          <strong>{number(active.length)}</strong>
        </article>
      </div>
      {canManage ? (
        <ExpenseForm
          conferenceId={conferenceId}
          client={client}
          onDraftChange={onDraftChange}
          onCreated={(record) => setRecords((current) => [record, ...current])}
        />
      ) : null}
      {operationMessage ? (
        <p className="form-message expense-operation-message" role="status">
          {t(operationMessage)}
        </p>
      ) : null}
      {status === "loading" ? (
        <p className="overview-state" aria-live="polite">
          {t("expense.loading")}
        </p>
      ) : status === "error" ? (
        <div className="overview-state" role="alert">
          <p>{t(message)}</p>
          <button
            className="button button--secondary"
            type="button"
            onClick={() => void load()}
          >
            {t("finance.action.tryAgain")}
          </button>
        </div>
      ) : records.length === 0 ? (
        <div className="overview-state">
          <h3>{t("expense.empty.heading")}</h3>
          <p>{t("expense.empty.description")}</p>
        </div>
      ) : (
        <div className="expense-table-wrap">
          <table className="expense-table">
            <thead>
              <tr>
                <th scope="col">{t("expense.column.title")}</th>
                <th scope="col">{t("expense.column.category")}</th>
                <th scope="col">{t("expense.column.date")}</th>
                <th scope="col">{t("expense.column.amount")}</th>
                <th scope="col">{t("expense.column.status")}</th>
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr
                  key={record.expenseId}
                  className={
                    record.status === "reversed"
                      ? "expense-table__reversed"
                      : undefined
                  }
                >
                  <th scope="row">
                    <bdi>{record.title}</bdi>
                  </th>
                  <td>{t(CATEGORY_KEYS[record.category])}</td>
                  <td>
                    <bdi>
                      {date(record.incurredAt, {
                        dateStyle: "medium",
                        timeStyle: "short",
                      })}
                    </bdi>
                  </td>
                  <td>
                    <RiyalAmount
                      minor={record.amountMinor}
                      currency={record.currency}
                      fractionDigits={2}
                    />
                  </td>
                  <td>
                    <div className="expense-status-actions">
                      <span
                        className={
                          record.status === "active"
                            ? "expense-status expense-status--active"
                            : "expense-status expense-status--reversed"
                        }
                      >
                        {t(
                          record.status === "active"
                            ? "expense.status.active"
                            : "expense.status.reversed",
                        )}
                      </span>
                      {canEdit && record.status === "active" ? (
                        <button
                          className="button button--quiet expense-edit"
                          type="button"
                          onClick={() => setEditingRecord(record)}
                        >
                          {t("expense.edit.action")}
                        </button>
                      ) : null}
                      {canManage && record.status === "active" ? (
                        <button
                          className="button button--quiet expense-reverse"
                          type="button"
                          disabled={reversingId === record.expenseId}
                          onClick={() => {
                            if (window.confirm(t("expense.reverse.confirm"))) {
                              void reverse(record);
                            }
                          }}
                        >
                          {reversingId === record.expenseId
                            ? t("expense.reverse.busy")
                            : t("expense.reverse.action")}
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editingRecord ? (
        <ExpenseEditDialog
          key={`${editingRecord.expenseId}:${editingRecord.revision}`}
          client={client}
          record={editingRecord}
          onClose={() => setEditingRecord(null)}
          onUpdated={updateRecord}
        />
      ) : null}
    </section>
  );
}

export function ExpensesPanel({
  client,
  role,
  onDraftChange,
}: {
  readonly client: ConferenceDashboardClient;
  readonly role: UserRole;
  readonly onDraftChange: RevenueDraftReporter;
}) {
  const { locale, t } = useLocale();
  const [conferences, setConferences] = useState<
    readonly PortfolioConferenceSummary[]
  >([]);
  const [conferenceId, setConferenceId] = useState("");
  const [status, setStatus] = useState<"loading" | "ready" | "error">(
    "loading",
  );
  const [hasDraft, setHasDraft] = useState(false);
  const reportDraft = useCallback(
    (draftId: string, active: boolean) => {
      setHasDraft(active);
      onDraftChange(draftId, active);
    },
    [onDraftChange],
  );

  useEffect(() => {
    const controller = new AbortController();
    void client
      .load(controller.signal)
      .then((overview) => {
        setConferences(overview.conferences);
        setConferenceId(overview.conferences[0]?.conferenceId ?? "");
        setStatus("ready");
      })
      .catch((error: unknown) => {
        if (!(error instanceof DOMException && error.name === "AbortError")) {
          setStatus("error");
        }
      });
    return () => controller.abort();
  }, [client]);

  if (status === "loading") {
    return <p className="overview-state">{t("expense.loading")}</p>;
  }
  if (status === "error") {
    return (
      <section className="overview-state" role="alert">
        <h2>{t("expense.unavailable.heading")}</h2>
        <p>{t("expense.unavailable.description")}</p>
      </section>
    );
  }
  if (!conferenceId) {
    return (
      <section className="overview-state">
        <h2>{t("expense.noConferences.heading")}</h2>
        <p>{t("expense.noConferences.description")}</p>
      </section>
    );
  }

  return (
    <section className="expense-workspace">
      <div className="revenue-toolbar">
        <label htmlFor="expense-conference">{t("expense.conference")}</label>
        <select
          id="expense-conference"
          value={conferenceId}
          disabled={hasDraft}
          onChange={(event) => setConferenceId(event.currentTarget.value)}
        >
          {conferences.map((conference) => (
            <option
              value={conference.conferenceId}
              key={conference.conferenceId}
            >
              {isolateText(localizedConferenceName(conference, locale))}
            </option>
          ))}
        </select>
      </div>
      <ConferenceExpenses
        key={conferenceId}
        conferenceId={conferenceId}
        client={client}
        canManage={role === "admin"}
        onDraftChange={reportDraft}
      />
    </section>
  );
}
